import { useState } from "react";
import { Helmet } from "react-helmet";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { CalendarIcon, CheckCircle, Megaphone, AlertTriangle, Truck } from "lucide-react";
import { cn } from "@/lib/utils";

const formSchema = z.object({
  departure_city: z.string().min(2, "La ville de départ est requise"),
  arrival_city: z.string().min(2, "La ville d'arrivée est requise"),
  stopover_cities: z.string().optional(),
  departure_date: z.date({ required_error: "La date de départ est requise" }),
  frequency: z.enum(["ponctuel", "regulier"]),
  transport_colis: z.boolean().default(false),
  transport_bagages: z.boolean().default(false),
  transport_autres: z.boolean().default(false),
  transport_autres_details: z.string().optional(),
  price_per_kg: z.string().min(1, "Le tarif par kg est requis"),
  max_weight_kg: z.string().optional(),
  conditions: z.string().optional(),
  carrier_name: z.string().min(2, "Votre nom ou nom commercial est requis"),
  carrier_phone: z.string().min(10, "Numéro de téléphone invalide"),
  carrier_email: z.string().email("Email invalide").optional().or(z.literal("")),
}).refine(
  (data) => data.transport_colis || data.transport_bagages || data.transport_autres,
  {
    message: "Sélectionnez au moins un type de transport",
    path: ["transport_colis"],
  }
);

type FormValues = z.infer<typeof formSchema>;

const NouvelleAnnonceTransporteur = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      departure_city: "",
      arrival_city: "",
      stopover_cities: "",
      frequency: "ponctuel",
      transport_colis: false,
      transport_bagages: false,
      transport_autres: false,
      transport_autres_details: "",
      price_per_kg: "",
      max_weight_kg: "",
      conditions: "",
      carrier_name: "",
      carrier_phone: "",
      carrier_email: "",
    },
  });

  const watchTransportAutres = form.watch("transport_autres");

  const onSubmit = async (values: FormValues) => {
    setIsSubmitting(true);
    try {
      const { error } = await supabase.from("carrier_listings").insert({
        departure_city: values.departure_city.trim(),
        arrival_city: values.arrival_city.trim(),
        stopover_cities: values.stopover_cities?.trim() || null,
        departure_date: format(values.departure_date, "yyyy-MM-dd"),
        frequency: values.frequency,
        transport_colis: values.transport_colis,
        transport_bagages: values.transport_bagages,
        transport_autres: values.transport_autres,
        transport_autres_details: values.transport_autres ? values.transport_autres_details?.trim() || null : null,
        price_per_kg: parseFloat(values.price_per_kg),
        max_weight_kg: values.max_weight_kg ? parseFloat(values.max_weight_kg) : null,
        conditions: values.conditions?.trim() || null,
        carrier_name: values.carrier_name.trim(),
        carrier_phone: values.carrier_phone.trim(),
        carrier_email: values.carrier_email?.trim() || null,
      });

      if (error) throw error;

      setIsSuccess(true);
      toast.success("Votre annonce a été envoyée avec succès !");
    } catch (error: any) {
      console.error("Erreur lors de l'envoi:", error);
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <>
        <Helmet>
          <title>Annonce envoyée | GoBledPro</title>
        </Helmet>
        <Header />
        <main className="min-h-screen bg-gradient-to-b from-background to-muted/20 py-12 md:py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-lg mx-auto text-center">
              <div className="w-20 h-20 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-primary" />
              </div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-4">
                Merci, votre annonce a bien été envoyée !
              </h1>
              <p className="text-muted-foreground mb-8">
                Elle sera visible prochainement sur la plateforme après vérification par notre équipe.
              </p>
              <Button asChild>
                <a href="/">Retour à l'accueil</a>
              </Button>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Publier une annonce de transport – Bêta | GoBledPro</title>
        <meta 
          name="description" 
          content="Publiez votre trajet France-Maroc et recevez des demandes de clients. Inscription gratuite, fonctionnalité en bêta test."
        />
        <link rel="canonical" href="https://gobledpro.com/nouvelle-annonce-transporteur" />
      </Helmet>

      <Header />

      <main className="min-h-screen bg-gradient-to-b from-background to-muted/20 py-8 md:py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 bg-secondary/20 text-secondary-foreground border border-secondary/40 px-4 py-1.5 rounded-full text-sm font-semibold mb-4">
                <Megaphone className="w-4 h-4" />
                Bêta
              </div>
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground mb-4">
                Publier une annonce de transport
              </h1>
              <p className="text-muted-foreground text-base md:text-lg max-w-xl mx-auto">
                Publiez votre trajet en quelques minutes.<br />
                <strong>Aucune inscription requise.</strong><br />
                <span className="text-sm">Cette fonctionnalité est en phase de test.</span>
              </p>
            </div>

            {/* Form */}
            <div className="bg-card border border-border rounded-2xl p-6 md:p-8 shadow-lg">
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                  
                  {/* Section: Informations du trajet */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 mb-4">
                      <Truck className="w-5 h-5 text-primary" />
                      <h2 className="text-lg font-heading font-semibold text-foreground">
                        Informations du trajet
                      </h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="departure_city"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Ville de départ *</FormLabel>
                            <FormControl>
                              <Input placeholder="Ex: Paris" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="arrival_city"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Ville d'arrivée *</FormLabel>
                            <FormControl>
                              <Input placeholder="Ex: Casablanca" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="stopover_cities"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Villes desservies (optionnel)</FormLabel>
                          <FormControl>
                            <Input placeholder="Ex: Lyon, Tanger, Rabat..." {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="departure_date"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Date de départ *</FormLabel>
                          <Popover>
                            <PopoverTrigger asChild>
                              <FormControl>
                                <Button
                                  variant="outline"
                                  className={cn(
                                    "w-full justify-start text-left font-normal",
                                    !field.value && "text-muted-foreground"
                                  )}
                                >
                                  <CalendarIcon className="mr-2 h-4 w-4" />
                                  {field.value ? (
                                    format(field.value, "PPP", { locale: fr })
                                  ) : (
                                    <span>Sélectionnez une date</span>
                                  )}
                                </Button>
                              </FormControl>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <Calendar
                                mode="single"
                                selected={field.value}
                                onSelect={field.onChange}
                                disabled={(date) => date < new Date()}
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="frequency"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Fréquence du trajet *</FormLabel>
                          <FormControl>
                            <RadioGroup
                              onValueChange={field.onChange}
                              defaultValue={field.value}
                              className="flex flex-wrap gap-4"
                            >
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem value="ponctuel" id="ponctuel" />
                                <label htmlFor="ponctuel" className="text-sm cursor-pointer">
                                  Ponctuel
                                </label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem value="regulier" id="regulier" />
                                <label htmlFor="regulier" className="text-sm cursor-pointer">
                                  Régulier
                                </label>
                              </div>
                            </RadioGroup>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Section: Type de transport */}
                  <div className="space-y-4">
                    <h2 className="text-lg font-heading font-semibold text-foreground">
                      Type de transport proposé *
                    </h2>

                    <div className="space-y-3">
                      <FormField
                        control={form.control}
                        name="transport_colis"
                        render={({ field }) => (
                          <FormItem className="flex items-center space-x-3 space-y-0">
                            <FormControl>
                              <Checkbox
                                checked={field.value}
                                onCheckedChange={field.onChange}
                              />
                            </FormControl>
                            <FormLabel className="font-normal cursor-pointer">
                              Colis
                            </FormLabel>
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="transport_bagages"
                        render={({ field }) => (
                          <FormItem className="flex items-center space-x-3 space-y-0">
                            <FormControl>
                              <Checkbox
                                checked={field.value}
                                onCheckedChange={field.onChange}
                              />
                            </FormControl>
                            <FormLabel className="font-normal cursor-pointer">
                              Bagages
                            </FormLabel>
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="transport_autres"
                        render={({ field }) => (
                          <FormItem className="flex items-center space-x-3 space-y-0">
                            <FormControl>
                              <Checkbox
                                checked={field.value}
                                onCheckedChange={field.onChange}
                              />
                            </FormControl>
                            <FormLabel className="font-normal cursor-pointer">
                              Autres
                            </FormLabel>
                          </FormItem>
                        )}
                      />

                      {watchTransportAutres && (
                        <FormField
                          control={form.control}
                          name="transport_autres_details"
                          render={({ field }) => (
                            <FormItem className="ml-7">
                              <FormControl>
                                <Input 
                                  placeholder="Précisez le type de transport..." 
                                  {...field} 
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      )}
                    </div>
                    {form.formState.errors.transport_colis && (
                      <p className="text-sm text-destructive">
                        {form.formState.errors.transport_colis.message}
                      </p>
                    )}
                  </div>

                  {/* Section: Capacité & tarif */}
                  <div className="space-y-4">
                    <h2 className="text-lg font-heading font-semibold text-foreground">
                      Capacité & Tarif
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="price_per_kg"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Tarif minimum par kg (€) *</FormLabel>
                            <FormControl>
                              <Input 
                                type="number" 
                                step="0.01" 
                                min="0" 
                                placeholder="Ex: 5.00" 
                                {...field} 
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="max_weight_kg"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Poids maximum accepté (kg)</FormLabel>
                            <FormControl>
                              <Input 
                                type="number" 
                                step="0.1" 
                                min="0" 
                                placeholder="Ex: 30" 
                                {...field} 
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="conditions"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Conditions particulières</FormLabel>
                          <FormControl>
                            <Textarea 
                              placeholder="Ex: Pas de produits liquides, paiement à la livraison..." 
                              rows={3}
                              {...field} 
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Section: Coordonnées */}
                  <div className="space-y-4">
                    <h2 className="text-lg font-heading font-semibold text-foreground">
                      Vos coordonnées
                    </h2>

                    <FormField
                      control={form.control}
                      name="carrier_name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Prénom ou nom commercial *</FormLabel>
                          <FormControl>
                            <Input placeholder="Ex: Mohamed ou TransportExpress" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="carrier_phone"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Numéro de téléphone *</FormLabel>
                            <FormControl>
                              <Input type="tel" placeholder="06 XX XX XX XX" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="carrier_email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email (optionnel)</FormLabel>
                            <FormControl>
                              <Input type="email" placeholder="votre@email.com" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  {/* Beta notice */}
                  <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                    <div className="flex gap-3">
                      <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-amber-800 dark:text-amber-200">
                        <strong>Fonctionnalité en bêta</strong><br />
                        Les annonces sont publiées manuellement pour le moment. 
                        Elles seront consultables ultérieurement via une barre de recherche.
                      </div>
                    </div>
                  </div>

                  {/* Submit button */}
                  <Button
                    type="submit"
                    size="lg"
                    className="w-full"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Envoi en cours..." : "Publier mon annonce"}
                  </Button>
                </form>
              </Form>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
};

export default NouvelleAnnonceTransporteur;
